import type { SupabaseClient } from '../../lib/supabase';
import type { CreateSectionInput, Section } from './types';

/**
 * Section writes. Reads come with the project workspace, so there is no
 * separate section fetch here.
 *
 * The database grants update on name, position and is_archived only; nothing
 * below sends anything else, and can_manage_project() decides who may write
 * at all.
 */

// prettier-ignore
const SECTION_COLUMNS = 'id, project_id, name, position, is_archived, created_by, created_at, updated_at';

function fail(message: string): never {
  throw new Error(message);
}

export async function createSection(
  client: SupabaseClient,
  input: CreateSectionInput & { createdBy: string },
): Promise<Section> {
  const { data, error } = await client
    .from('sections')
    .insert({
      project_id: input.projectId,
      name: input.name.trim(),
      position: input.position,
      created_by: input.createdBy,
    })
    .select(SECTION_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return data;
}

export async function renameSection(
  client: SupabaseClient,
  sectionId: string,
  name: string,
): Promise<Section> {
  const { data, error } = await client
    .from('sections')
    .update({ name: name.trim() })
    .eq('id', sectionId)
    .select(SECTION_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * Archiving hides a section without deleting it, so any task still inside it
 * survives. There is no delete path in the interface for that reason.
 */
export async function setSectionArchived(
  client: SupabaseClient,
  sectionId: string,
  isArchived: boolean,
): Promise<Section> {
  const { data, error } = await client
    .from('sections')
    .update({ is_archived: isArchived })
    .eq('id', sectionId)
    .select(SECTION_COLUMNS)
    .single();

  if (error) {
    fail(error.message);
  }
  return data;
}

/**
 * Applies a set of position changes. Two rows swap in the common case, so the
 * updates are sent in sequence and the first failure stops the rest rather
 * than leaving a half-applied order behind quietly.
 */
export async function reorderSections(
  client: SupabaseClient,
  updates: { id: string; position: number }[],
): Promise<void> {
  for (const update of updates) {
    const { error } = await client
      .from('sections')
      .update({ position: update.position })
      .eq('id', update.id);

    if (error) {
      fail(error.message);
    }
  }
}

export const SECTION_ERROR_MESSAGES: Record<string, string> = {
  sections_name_present: 'Enter a section name.',
  sections_position_positive: 'That section position is not valid.',
};

/** Turns a database error message into something a person can act on. */
export function sectionErrorMessage(message: string): string {
  const known = Object.keys(SECTION_ERROR_MESSAGES).find((key) => message.includes(key));
  if (known) {
    return SECTION_ERROR_MESSAGES[known] as string;
  }
  if (message.toLowerCase().includes('permission denied')) {
    return 'You do not have permission to change sections in this project.';
  }
  return 'That change could not be saved. Try again.';
}
